import { useEffect, useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useAddStore } from "@/hooks/usePosStoreMutations";
import { useToast } from "@/hooks/use-toast";
import {
  DUPLICATE_LOCATION_MESSAGE,
  findDuplicateStore,
  validateRequiredField,
  validateStoreId,
  type PosStore,
} from "@/utils/posStores";

interface AddStoreDialogProps {
  /** Existing brands, for the combobox and its "New brand: X" option. */
  brands: readonly string[];
  /** The current registry, so a (brand, location) pair that already exists
   *  is refused before insert -- the table has no uniqueness on names. */
  stores: readonly PosStore[];
  onCreated?: (store: PosStore) => void;
}


/**
 * "Add store": lets an operator add a new location to an existing brand, or
 * onboard a brand new brand, without a migration (captain's ask). Kept as its
 * own dialog component so OrderSpy.tsx only needs a trigger button.
 */
export function AddStoreDialog({ brands, stores, onCreated }: AddStoreDialogProps) {
  const [open, setOpen] = useState(false);
  const [brandQuery, setBrandQuery] = useState("");
  const [brandListOpen, setBrandListOpen] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [storeId, setStoreId] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(blurTimer.current), []);
  const addStore = useAddStore();
  const { toast } = useToast();

  const brandMatches = useMemo(() => {
    const q = brandQuery.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.toLowerCase().includes(q));
  }, [brands, brandQuery]);

  const matchedBrand = brands.find((b) => b.toLowerCase() === brandQuery.trim().toLowerCase());
  const exactBrandMatch = matchedBrand !== undefined;
  const canonicalBrand = matchedBrand ?? brandQuery.trim();

  const reset = () => {
    setBrandQuery("");
    setBrandListOpen(false);
    setLocationName("");
    setStoreId("");
    setSubmitted(false);
    addStore.reset();
  };

  const brandError = submitted ? validateRequiredField(brandQuery, "Brand") : null;
  const validateLocation = () =>
    validateRequiredField(locationName, "Location name") ??
    (findDuplicateStore(stores, canonicalBrand, locationName) ? DUPLICATE_LOCATION_MESSAGE : null);
  const locationError = submitted ? validateLocation() : null;
  const storeIdError = submitted ? validateStoreId(storeId) : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (validateRequiredField(brandQuery, "Brand") || validateLocation() || validateStoreId(storeId)) {
      return;
    }

    addStore.mutate(
      { brand: canonicalBrand, location_name: locationName, store_id: storeId },
      {
        onSuccess: (store) => {
          toast({ title: "Store added", description: `${store.brand}: ${store.location_name}` });
          onCreated?.(store);
          setOpen(false);
          reset();
        },
        onError: (error) => {
          toast({
            title: "Couldn't add store",
            description: error instanceof Error ? error.message : String(error),
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" className="h-11">
          <Plus className="w-4 h-4 mr-1.5" />
          Add store
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add store</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <Label htmlFor="add-store-brand">Brand</Label>
            <Input
              id="add-store-brand"
              className="h-11 text-base mt-1.5"
              autoComplete="off"
              value={brandQuery}
              placeholder="Search or type a new brand…"
              onChange={(e) => {
                setBrandQuery(e.target.value);
                setBrandListOpen(true);
              }}
              onFocus={() => setBrandListOpen(true)}
              onBlur={() => {
                clearTimeout(blurTimer.current);
                blurTimer.current = setTimeout(() => setBrandListOpen(false), 100);
              }}
            />
            {brandListOpen && (
              <ul className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-border bg-card shadow-md">
                {brandMatches.map((b) => (
                  <li
                    key={b}
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-muted"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setBrandQuery(b);
                      setBrandListOpen(false);
                    }}
                  >
                    {b}
                  </li>
                ))}
                {brandQuery.trim() && !exactBrandMatch && (
                  <li
                    className="px-3 py-2 text-sm cursor-pointer hover:bg-muted text-status-info"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setBrandListOpen(false);
                    }}
                  >
                    New brand: {brandQuery.trim()}
                  </li>
                )}
                {brandMatches.length === 0 && !brandQuery.trim() && (
                  <li className="px-3 py-2 text-sm text-muted-foreground">Start typing…</li>
                )}
              </ul>
            )}
            {brandError && <p className="text-sm text-status-danger mt-1">{brandError}</p>}
          </div>

          <div>
            <Label htmlFor="add-store-location">Location name</Label>
            <Input
              id="add-store-location"
              className="h-11 text-base mt-1.5"
              value={locationName}
              placeholder="e.g. Fresno/West Shaw"
              onChange={(e) => setLocationName(e.target.value)}
            />
            {locationError && <p className="text-sm text-status-danger mt-1">{locationError}</p>}
          </div>

          <div>
            <Label htmlFor="add-store-id">Adora store ID</Label>
            <Input
              id="add-store-id"
              className="h-11 text-base mt-1.5 font-mono"
              value={storeId}
              placeholder="5 letters/numbers, or blank if not on Adora yet"
              onChange={(e) => setStoreId(e.target.value)}
            />
            {storeIdError && <p className="text-sm text-status-danger mt-1">{storeIdError}</p>}
          </div>

          <DialogFooter>
            <Button type="submit" disabled={addStore.isPending} className="h-11">
              {addStore.isPending ? "Adding…" : "Add store"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddStoreDialog;
